import { create } from 'zustand';
import api from '../utils/api';

// Types matching backend schema
export type FilterConditionField = 'from' | 'to' | 'subject' | 'body' | 'hasAttachment' | 'size';
export type FilterConditionOperator = 'contains' | 'not_contains' | 'equals' | 'not_equals' | 'starts_with' | 'ends_with' | 'greater_than' | 'less_than' | 'matches_regex';
export type FilterActionType = 'move_to_folder' | 'add_label' | 'mark_as_read' | 'mark_as_starred' | 'mark_as_spam' | 'delete' | 'forward' | 'mark_as_important';

export interface FilterCondition {
  field: FilterConditionField;
  operator: FilterConditionOperator;
  value: string | number | boolean;
}

export interface FilterAction {
  type: FilterActionType;
  value?: string;
}

export interface Filter {
  _id?: string;
  id?: string; // For frontend compatibility
  userId?: string;
  name: string;
  conditions: FilterCondition[];
  actions: FilterAction[];
  matchType: 'all' | 'any';
  isActive: boolean;
  stopProcessing: boolean;
  order: number;
  createdAt?: string;
  updatedAt?: string;
}

interface FiltersState {
  filters: Filter[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface FiltersActions {
  fetchFilters: () => Promise<void>;
  createFilter: (data: Omit<Filter, '_id' | 'userId' | 'order' | 'createdAt' | 'updatedAt'>) => Promise<Filter>;
  updateFilter: (id: string, data: Partial<Filter>) => Promise<Filter>;
  deleteFilter: (id: string) => Promise<void>;
  toggleFilter: (id: string) => Promise<Filter>;
  reorderFilters: (filterIds: string[]) => Promise<void>;
  clearError: () => void;
}

type FiltersStore = FiltersState & FiltersActions;

export const useFiltersStore = create<FiltersStore>((set, get) => ({
  filters: [],
  isLoading: false,
  isSaving: false,
  error: null,

  fetchFilters: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get('/mail/filters');
      const filters = response.data?.data || response.data || [];
      console.log('📋 Fetched filters:', filters);
      set({ filters, isLoading: false });
    } catch (error: any) {
      console.error('Failed to fetch filters:', error);
      set({
        error: error.response?.data?.message || 'Failed to fetch filters',
        isLoading: false,
      });
    }
  },

  createFilter: async (data) => {
    set({ isSaving: true, error: null });
    try {
      const response = await api.post('/mail/filters', data);
      const newFilter = response.data?.data || response.data;
      console.log('➕ Created filter:', newFilter);
      set((state) => ({
        filters: [...state.filters, newFilter],
        isSaving: false,
      }));
      return newFilter;
    } catch (error: any) {
      console.error('Failed to create filter:', error);
      set({
        error: error.response?.data?.message || 'Failed to create filter',
        isSaving: false,
      });
      throw error;
    }
  },

  updateFilter: async (id, data) => {
    set({ isSaving: true, error: null });
    try {
      const response = await api.put(`/mail/filters/${id}`, data);
      const updatedFilter = response.data?.data || response.data;
      console.log('✏️ Updated filter:', updatedFilter);
      set((state) => ({
        filters: state.filters.map((f) =>
          (f._id === id || f.id === id) ? updatedFilter : f
        ),
        isSaving: false,
      }));
      return updatedFilter;
    } catch (error: any) {
      console.error('Failed to update filter:', error);
      set({
        error: error.response?.data?.message || 'Failed to update filter',
        isSaving: false,
      });
      throw error;
    }
  },

  deleteFilter: async (id) => {
    set({ isSaving: true, error: null });
    try {
      await api.delete(`/mail/filters/${id}`);
      console.log('🗑️ Deleted filter:', id);
      set((state) => ({
        filters: state.filters.filter((f) => f._id !== id && f.id !== id),
        isSaving: false,
      }));
    } catch (error: any) {
      console.error('Failed to delete filter:', error);
      set({
        error: error.response?.data?.message || 'Failed to delete filter',
        isSaving: false,
      });
      throw error;
    }
  },

  toggleFilter: async (id) => {
    set({ isSaving: true, error: null });
    try {
      const response = await api.post(`/mail/filters/${id}/toggle`);
      const toggledFilter = response.data?.data || response.data;
      console.log('🔄 Toggled filter:', toggledFilter);
      set((state) => ({
        filters: state.filters.map((f) =>
          (f._id === id || f.id === id) ? toggledFilter : f
        ),
        isSaving: false,
      }));
      return toggledFilter;
    } catch (error: any) {
      console.error('Failed to toggle filter:', error);
      set({
        error: error.response?.data?.message || 'Failed to toggle filter',
        isSaving: false,
      });
      throw error;
    }
  },

  reorderFilters: async (filterIds) => {
    set({ isSaving: true, error: null });
    try {
      await api.put('/mail/filters/reorder', { filterIds });
      console.log('🔄 Reordered filters');
      // Refresh filters to get new order
      await get().fetchFilters();
      set({ isSaving: false });
    } catch (error: any) {
      console.error('Failed to reorder filters:', error);
      set({
        error: error.response?.data?.message || 'Failed to reorder filters',
        isSaving: false,
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));

export default useFiltersStore;
